import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { COLOR_MAP } from "../../../../../constants/ColorNames";
import { ITemplate } from "../../../../../types/campaign.types";
import { ITemplateDetails } from "./MiddleSection";
import { useSelector } from "react-redux";
import { draftDetailsSelector } from "../../../../../redux/selectors/whatsAppCampaign.selector";

interface IProps extends ITemplateDetails {}

const ContentTemplates: React.FC<IProps> = ({
  selectedTemplate,
  handleUpdateSelectedTemplate,
  templates,
  fetchStatus,
}) => {
  const [scrollPosition, setScrollPosition] = useState(0);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { draftDetails } = useSelector(draftDetailsSelector);

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  const handleScroll = () => {
    if (scrollRef?.current) {
      const scrollPosition = scrollRef.current?.scrollLeft;
      setScrollPosition(scrollPosition);
    }
  };

  const handleSelectedTemplate = (selectedTemplate: ITemplate) => {
    handleUpdateSelectedTemplate(selectedTemplate);
  };

  useEffect(() => {
    const isSelectedTemplatesInTemplates = templates?.find(
      (item) => item.id === selectedTemplate?.id
    );
    // to update the selected template when no template is selected
    if (
      (!selectedTemplate || !isSelectedTemplatesInTemplates) &&
      !!templates?.length
    ) {
      // to update selected template from draft
      const selectedTemplateFromDraft = templates.find(
        (template) =>
          template?.id === `${draftDetails?.whatsAppDetails?.templateId}`
      );

      // current selectedTemplate index
      let currentSelectedTemplateIndex = templates?.findIndex(
        (item) =>
          item.name?.toLowerCase() === selectedTemplate?.name?.toLowerCase()
      );

      if (
        selectedTemplateFromDraft &&
        draftDetails?.whatsAppDetails?.templateId &&
        currentSelectedTemplateIndex === -1
      ) {
        // when template is available in draft as well as in templates
        handleUpdateSelectedTemplate(selectedTemplateFromDraft);
      } else if (
        !selectedTemplateFromDraft &&
        draftDetails?.whatsAppDetails?.templateId
      ) {
        // when template is available in draft but not in templates
        handleUpdateSelectedTemplate(
          templates[
            currentSelectedTemplateIndex === -1
              ? 0
              : currentSelectedTemplateIndex
          ]
        );
      } else {
        handleUpdateSelectedTemplate(
          templates[
            currentSelectedTemplateIndex === -1
              ? 0
              : currentSelectedTemplateIndex
          ]
        );
      }
    } else if (!templates?.length) {
      handleUpdateSelectedTemplate(null);
    }
  }, [
    draftDetails?.whatsAppDetails?.templateId,
    handleUpdateSelectedTemplate,
    selectedTemplate,
    templates,
  ]);

  // useEffect(() => {
  //   if (!selectedTemplate) {
  //     // to update selected template from draft
  //     const selectedTemplateFromDraft = templates.find(
  //       (template) =>
  //         template?.id === `${draftDetails?.whatsAppDetails?.templateId}`
  //     );

  //     if (
  //       selectedTemplateFromDraft &&
  //       draftDetails?.whatsAppDetails?.templateId
  //     ) {
  //       // when template is available in draft as well as in templates
  //       handleUpdateSelectedTemplate(selectedTemplateFromDraft);
  //     } else if (
  //       !selectedTemplateFromDraft &&
  //       draftDetails?.whatsAppDetails?.templateId
  //     ) {
  //       // when template is available in draft but not in templates
  //       handleUpdateSelectedTemplate(templates[0]);
  //     }
  //   }
  // }, [
  //   draftDetails?.whatsAppDetails?.templateId,
  //   handleUpdateSelectedTemplate,
  //   templates,
  //   selectedTemplate,
  // ]);

  return (
    <Box
      sx={{
        backgroundColor: COLOR_MAP.WHITE,
        p: 2,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: "fontWeightBold" }}>
        Select template
      </Typography>

      <Box
        sx={{
          position: "relative",
          width: "100%",
        }}
      >
        <IconButton
          onClick={handleScrollLeft}
          sx={{
            position: "absolute",
            left: 0,
            top: "65%",
            transform: "translateY(-50%)",
            visibility: scrollPosition > 0 ? "visible" : "hidden",
            border: "1px solid #ccc",
            backgroundColor: COLOR_MAP.WHITE,
            boxShadow: "0px 0px 30px 30px rgba(247,247,247,1)",
            "&: hover": {
              backgroundColor: COLOR_MAP.WHITE,
            },
          }}
        >
          <KeyboardArrowLeftIcon sx={{ fontSize: 28 }} />
        </IconButton>
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          sx={{
            overflowX: "auto",
            width: "100%",
            display: "flex",
            pt: 2,
            whiteSpace: "nowrap",
            boxSizing: "border-box",

            // to hide scrollbar
            "&::-webkit-scrollbar": {
              display: "none",
            },
            "-ms-overflow-style": "none",
            "scrollbar-width": "none",
          }}
        >
          {fetchStatus === "pending" && (
            <Typography variant="h6" sx={{ color: COLOR_MAP.GREY }}>
              Loading...
            </Typography>
          )}
          {fetchStatus === "error" && (
            <Typography variant="h6" sx={{ color: COLOR_MAP.ERROR }}>
              Failed to fetch templates!
            </Typography>
          )}
          {fetchStatus === "success" &&
            (!!templates?.length ? (
              templates?.map((template) => {
                const { id, name } = template;
                return (
                  <Box
                    key={id}
                    sx={{
                      borderRadius: 8,
                      border: "2px solid #ccc",
                      p: 1,
                      cursor: "pointer",
                      borderColor:
                        selectedTemplate?.id === id
                          ? COLOR_MAP.DARK_PURPLE
                          : "#ccc",
                      mr: 1,
                      maxWidth: 200,
                      minWidth: "max-content",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                    onClick={() => handleSelectedTemplate(template)}
                  >
                    <Typography variant="h6">{name}</Typography>
                  </Box>
                );
              })
            ) : (
              <Typography variant="h6">*No templates available</Typography>
            ))}
        </Box>
        <IconButton
          onClick={handleScrollRight}
          sx={{
            position: "absolute",
            right: 0,
            top: "65%",
            transform: "translateY(-50%)",
            visibility:
              scrollPosition <
              (scrollRef?.current?.scrollWidth || 0) -
                (scrollRef?.current?.clientWidth || 0) -
                2
                ? "visible"
                : "hidden",
            border: "1px solid #ccc",
            backgroundColor: COLOR_MAP.WHITE,
            boxShadow: "0px 0px 30px 30px rgba(247,247,247,1)",
            "&: hover": {
              backgroundColor: COLOR_MAP.WHITE,
            },
          }}
        >
          <KeyboardArrowRightIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default React.memo(ContentTemplates);
